import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Play, Pause, Volume2, VolumeX, Maximize, ArrowLeft, SkipForward, Subtitles,
  Settings, RotateCcw, RotateCw, ListVideo, X, ChevronRight,
} from "lucide-react";

function fmt(t) {
  if (!t || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function Watch() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isTrailer = params.get("trailer") === "1";
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const hideTimer = useRef(null);
  const [item, setItem] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [subs, setSubs] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [epPanel, setEpPanel] = useState(false);
  const [season, setSeason] = useState(1);
  const [epNum, setEpNum] = useState(1);

  useEffect(() => {
    api.get(`/content/${id}`).then((r) => {
      setItem(r.data);
      setSeason(parseInt(params.get("s")) || 1);
      setEpNum(parseInt(params.get("e")) || 1);
    }).catch(() => navigate("/"));
  }, [id]);

  const isSeries = item?.type === "series" && item?.episodes?.length > 0;

  const currentEpisode = useMemo(() => {
    if (!isSeries) return null;
    const s = item.episodes.find((x) => x.season === season) || item.episodes[0];
    return s.episodes.find((e) => e.number === epNum) || s.episodes[0];
  }, [item, isSeries, season, epNum]);

  const src = useMemo(() => {
    if (!item) return "";
    if (isTrailer) return item.trailer_url;
    if (isSeries && currentEpisode) return currentEpisode.video_url;
    return item.video_url;
  }, [item, isTrailer, isSeries, currentEpisode]);

  const saveProgress = useCallback(() => {
    const v = videoRef.current;
    if (v && user && !isTrailer && v.currentTime > 3) {
      api.post("/progress", { content_id: id, position: v.currentTime, duration: v.duration || 0 }).catch(() => {});
    }
  }, [id, user, isTrailer]);

  useEffect(() => () => saveProgress(), [saveProgress]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.then(() => setPlaying(true)).catch((e) => {
          console.warn("Play error suppressed:", e);
          setPlaying(false);
        });
      } else {
        setPlaying(true);
      }
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const onTime = () => {
    const v = videoRef.current;
    setCurrent(v.currentTime);
    if (v.currentTime > 10) setShowIntro(false);
  };

  const seek = (e) => {
    const v = videoRef.current;
    const val = parseFloat(e.target.value);
    v.currentTime = val;
    setCurrent(val);
  };

  const skip = (secs) => {
    const v = videoRef.current;
    if (v) v.currentTime = Math.min(Math.max(0, v.currentTime + secs), duration);
  };

  const changeVolume = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    videoRef.current.volume = val;
    setMuted(val === 0);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const goFull = () => {
    const wrap = wrapRef.current;
    const v = videoRef.current;
    try {
      if (document.fullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      } else if (wrap && wrap.requestFullscreen) {
        wrap.requestFullscreen().catch(() => {
          if (v && v.webkitEnterFullscreen) {
            try { v.webkitEnterFullscreen(); } catch {}
          }
        });
      } else if (v && v.webkitEnterFullscreen) {
        try { v.webkitEnterFullscreen(); } catch {}
      }
    } catch (err) {
      console.warn("Fullscreen not supported:", err);
    }
  };

  const wakeControls = () => {
    setShowControls(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => { if (!videoRef.current?.paused) setShowControls(false); }, 3000);
  };

  const seasonList = isSeries ? item.episodes.find((x) => x.season === season)?.episodes || [] : [];

  const playEpisode = (s, e) => {
    setSeason(s);
    setEpNum(e);
    setShowIntro(true);
    setEpPanel(false);
    setTimeout(() => {
      const v = videoRef.current;
      if (v) {
        const p = v.play();
        if (p && typeof p.catch === "function") {
          p.then(() => setPlaying(true)).catch(() => setPlaying(false));
        }
      }
    }, 100);
  };

  const nextEpisode = () => {
    if (!isSeries) return;
    const idx = seasonList.findIndex((e) => e.number === epNum);
    if (idx >= 0 && idx < seasonList.length - 1) {
      playEpisode(season, seasonList[idx + 1].number);
    } else {
      const nextS = item.episodes.find((x) => x.season === season + 1);
      if (nextS) playEpisode(season + 1, nextS.episodes[0].number);
    }
  };

  const ytId = getYouTubeId(src);
  const isYouTube = !!ytId;

  return (
    <div ref={wrapRef} className="fixed inset-0 bg-black z-[60]" onMouseMove={wakeControls} data-testid="nikplay-video-player">
      {isYouTube ? (
        <iframe
          title={item?.title || "player"}
          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&playsinline=1&modestbranding=1`}
          className="w-full h-full"
          style={{ border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          data-testid="youtube-embed"
        />
      ) : (
        <video
          ref={videoRef}
          src={src || undefined}
          key={src}
          className="w-full h-full object-contain bg-black"
          playsInline
          webkit-playsinline="true"
          onClick={togglePlay}
          onTimeUpdate={onTime}
          onLoadedMetadata={(e) => {
            setDuration(e.target.duration);
            const p = e.target.play();
            if (p && typeof p.catch === "function") {
              p.then(() => setPlaying(true)).catch(() => {
                setPlaying(false);
              });
            }
          }}
          onEnded={nextEpisode}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        >
          {subs && <track kind="captions" default label="English" />}
        </video>
      )}

      {/* Top bar */}
      <div className={`absolute top-0 inset-x-0 p-4 md:p-6 bg-gradient-to-b from-black/80 to-transparent transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0"}`}>
        <div className="flex items-center justify-between">
          <button data-testid="player-back-btn" onClick={() => { saveProgress(); navigate(-1); }} className="flex items-center gap-2 text-white hover:text-slate-300">
            <ArrowLeft size={24} />
            <span className="font-display font-semibold">
              {item?.title}{isTrailer ? " — Trailer" : isSeries && currentEpisode ? ` — ${currentEpisode.title}` : ""}
            </span>
          </button>
          {isSeries && !isTrailer && (
            <button data-testid="episodes-toggle-btn" onClick={() => setEpPanel(true)} className="flex items-center gap-2 text-white bg-white/10 border border-white/20 px-3 py-1.5 rounded-lg hover:bg-white/20 transition-colors text-sm">
              <ListVideo size={17} /> Episodes
            </button>
          )}
        </div>
      </div>

      {/* Center play */}
      {!isYouTube && !playing && (
        <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center hover:scale-105 transition-transform">
            <Play size={38} className="text-black ml-1" fill="currentColor" />
          </div>
        </button>
      )}

      {/* Skip intro */}
      {!isYouTube && showIntro && playing && !isTrailer && (
        <button data-testid="skip-intro-btn" onClick={() => skip(30)}
          className="absolute bottom-28 right-6 md:right-10 flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/40 text-white px-4 py-2 rounded-lg hover:bg-white/25 transition-colors">
          <SkipForward size={17} /> Skip Intro
        </button>
      )}

      {/* Episodes panel */}
      {epPanel && (
        <div className="absolute inset-y-0 right-0 w-full sm:w-[420px] bg-[#0B0E14]/97 backdrop-blur-xl border-l border-white/10 z-50 flex flex-col" data-testid="episodes-panel">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <span className="font-display font-bold text-white">Episodes</span>
            <button onClick={() => setEpPanel(false)}><X size={20} className="text-slate-400" /></button>
          </div>
          <div className="flex gap-2 p-4 border-b border-white/10 overflow-x-auto no-scrollbar">
            {item.episodes.map((s) => (
              <button key={s.season} onClick={() => setSeason(s.season)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap ${season === s.season ? "bg-[var(--nik-blue)] text-white" : "bg-white/5 text-slate-300"}`}>
                Season {s.season}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {seasonList.map((e) => {
              const active = e.number === epNum;
              return (
                <button key={e.number} data-testid={`episode-${season}-${e.number}`} onClick={() => playEpisode(season, e.number)}
                  className={`w-full flex gap-3 p-2 rounded-xl text-left hover:bg-white/5 ${active ? "bg-white/10" : ""}`}>
                  <div className="relative w-28 h-16 rounded-lg overflow-hidden shrink-0">
                    <img src={e.thumbnail} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      {active ? <Play size={18} className="text-[var(--nik-glow)]" fill="currentColor" /> : <Play size={16} className="text-white/80" />}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{e.title}</div>
                    <div className="text-xs text-slate-500 font-mono">{e.duration}</div>
                    <div className="text-xs text-slate-400 line-clamp-2 mt-0.5">{e.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom controls */}
      {!isYouTube && (
      <div className={`absolute bottom-0 inset-x-0 px-4 md:px-8 pb-5 pt-14 bg-gradient-to-t from-black/90 to-transparent transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0"}`}>
        <input type="range" min={0} max={duration || 0} value={current} onChange={seek} data-testid="player-seekbar"
          className="w-full h-1.5 accent-[var(--nik-blue)] cursor-pointer mb-3"
          style={{ background: `linear-gradient(to right, var(--nik-blue) ${(current / (duration || 1)) * 100}%, rgba(255,255,255,0.25) 0)` }} />
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-4">
            <button data-testid="player-play-btn" onClick={togglePlay} className="hover:text-slate-300">
              {playing ? <Pause size={26} fill="currentColor" /> : <Play size={26} fill="currentColor" />}
            </button>
            <button onClick={() => skip(-10)} className="hover:text-slate-300"><RotateCcw size={22} /></button>
            <button onClick={() => skip(10)} className="hover:text-slate-300"><RotateCw size={22} /></button>
            {isSeries && !isTrailer && (
              <button data-testid="next-episode-btn" onClick={nextEpisode} title="Next Episode" className="hover:text-slate-300"><ChevronRight size={26} /></button>
            )}
            <div className="flex items-center gap-2 group/vol">
              <button onClick={toggleMute} className="hover:text-slate-300">
                {muted || volume === 0 ? <VolumeX size={22} /> : <Volume2 size={22} />}
              </button>
              <input type="range" min={0} max={1} step={0.05} value={muted ? 0 : volume} onChange={changeVolume}
                className="w-0 group-hover/vol:w-20 transition-all duration-300 h-1 accent-white cursor-pointer" />
            </div>
            <span className="font-mono text-sm text-slate-200">{fmt(current)} / {fmt(duration)}</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => setSubs((s) => !s)} className={`hover:text-slate-300 ${subs ? "text-[var(--nik-blue)]" : ""}`} title="Subtitles">
              <Subtitles size={22} />
            </button>
            <button className="hover:text-slate-300" title="Quality"><Settings size={22} /></button>
            <button onClick={goFull} className="hover:text-slate-300" title="Fullscreen"><Maximize size={22} /></button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
